import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Avatar,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Collapse,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { adminListUsers } from "../api";
import UserPayinGatewaysCard from "../components/UserPayinGatewaysCard";

const ROLE_COLORS = {
  ADMIN: "#1c6b8c",
  ADMIN_PARTNER: "#2f8fb5",
  WHITE_LABEL: "#a855f7",
  STATE_HEAD: "#0ea5e9",
  SUPER_DISTRIBUTOR: "#06b6d4",
  MASTER_DISTRIBUTOR: "#14b8a6",
  DISTRIBUTOR: "#22c55e",
  RETAILER: "#f59e0b",
  SHOPKEEPER: "#f97316",
};

function formatDate(val) {
  if (val == null) return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function UserRow({ user, onView, onGateways }) {
  const [open, setOpen] = useState(false);
  const roleName = user.role_name ?? user.roleName;
  const firstName = user.first_name ?? user.firstName ?? "";
  const lastName = user.last_name ?? user.lastName ?? "";
  const isActive = user.is_active ?? user.isActive ?? true;
  const parentName = user.parent_name ?? user.parentName;
  return (
    <>
      <TableRow
        hover
        sx={{ "& > *": { borderBottom: open ? "none" : undefined } }}
      >
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? (
              <KeyboardArrowUpRoundedIcon />
            ) : (
              <KeyboardArrowDownRoundedIcon />
            )}
          </IconButton>
        </TableCell>
        <TableCell>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: ROLE_COLORS[roleName] || "#1c6b8c",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {firstName[0]}
              {lastName[0]}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {firstName} {lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user.email || user.mobile_number || "—"}
              </Typography>
            </Box>
          </Stack>
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={(roleName ?? "").replace(/_/g, " ")}
            sx={{
              bgcolor: `${ROLE_COLORS[roleName] || "#1c6b8c"}15`,
              color: ROLE_COLORS[roleName] || "#1c6b8c",
              fontWeight: 600,
              fontSize: 11,
            }}
          />
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={isActive ? "Active" : "Inactive"}
            color={isActive ? "success" : "default"}
          />
        </TableCell>
        <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
          ₹{Number(user.main_balance || 0).toLocaleString("en-IN")}
        </TableCell>
        <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
          ₹{Number(user.commission_balance || 0).toLocaleString("en-IN")}
        </TableCell>
        <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>
          {parentName || "—"}
        </TableCell>
        <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>
          {formatDate(user.created_at ?? user.createdAt)}
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={() => onView(user.id)}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 12 }}
            >
              View
            </Button>
            <Tooltip title="Payin gateways" arrow>
              <IconButton
                size="small"
                onClick={() =>
                  onGateways({
                    id: user.id,
                    name: `${firstName} ${lastName}`.trim(),
                  })
                }
              >
                <CreditCardRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 2 }}>
              <Stack direction="row" gap={4} flexWrap="wrap">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body2">
                    {user.mobile_number || user.mobileNumber || "—"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body2">{user.email || "—"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    City
                  </Typography>
                  <Typography variant="body2">{user.city || "—"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    State
                  </Typography>
                  <Typography variant="body2">{user.state || "—"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    PAN
                  </Typography>
                  <Typography variant="body2">
                    {user.pan_card_number || user.panCardNumber || "—"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Deposit Limit
                  </Typography>
                  <Typography variant="body2">
                    ₹
                    {Number(
                      user.deposit_limit ?? user.depositLimit ?? 0,
                    ).toLocaleString("en-IN")}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Withdraw Limit
                  </Typography>
                  <Typography variant="body2">
                    ₹
                    {Number(
                      user.withdraw_daily_limit ?? user.withdrawDailyLimit ?? 0,
                    ).toLocaleString("en-IN")}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [gwUser, setGwUser] = useState(null); // { id, name } for the gateways dialog

  const fetchUsers = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 20 };
    if (search) params.search = search;
    if (roleFilter !== "ALL") params.roleName = roleFilter;
    adminListUsers(params)
      .then(({ data }) => {
        setUsers(data.users || []);
        setTotal(data.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, search, roleFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return (
    <Box>
      <Typography variant="h5" mb={0.5}>
        All Users
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {total} total users in the system
      </Typography>

      <Stack direction="row" gap={2} mb={3} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Search name, email, phone..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon
                  sx={{
                    fontSize: 20,
                    color: "text.secondary",
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 260 }}
        />
        <TextField
          select
          size="small"
          value={roleFilter}
          onChange={(e) => {
            setRoleFilter(e.target.value);
            setPage(1);
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="ALL">All Roles</MenuItem>
          {Object.keys(ROLE_COLORS).map((r) => (
            <MenuItem key={r} value={r}>
              {r.replace(/_/g, " ")}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Card sx={{ overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : users.length === 0 ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <PeopleAltRoundedIcon
              sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
            />
            <Typography color="text.secondary">No users found</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Main Bal.</TableCell>
                <TableCell>Comm. Bal.</TableCell>
                <TableCell>Parent</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  onView={(id) => navigate(`/admin/users/${id}`)}
                  onGateways={setGwUser}
                />
              ))}
            </TableBody>
          </Table>
          </Box>
        )}
        {Math.ceil(total / 20) > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 2,
            }}
          >
            <Pagination
              count={Math.ceil(total / 20)}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
            />
          </Box>
        )}
      </Card>

      <Dialog
        open={!!gwUser}
        onClose={() => setGwUser(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <span>{gwUser?.name || "User"} — Payin Gateways</span>
          <IconButton size="small" onClick={() => setGwUser(null)}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {gwUser && <UserPayinGatewaysCard userId={gwUser.id} />}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
